export const foodPageData = {
  stats: [
    { value: "25%", label: "Procurement Cost Savings" },
    { value: "2.5h", label: "Staff Time Saved Daily" },
    { value: "40%", label: "Inventory Cost Reduction" },
    { value: "95%", label: "Fewer Order Errors" },
  ],
  statsAccent: "from-emerald-600 to-green-700",

  challengeTitle: "Consumer Goods Procurement Is Costing You 2.5 Hours Every Day",
  challengeSubtitle:
    "Manual processes, fragmented suppliers, and reactive inventory are quietly destroying your margins and your team's productivity.",
  challenges: [
    {
      icon: "🗂️",
      title: "Multi-Supplier Coordination Chaos",
      description:
        "Managing dozens of suppliers across multiple categories with no unified system leads to missed orders and poor relationships.",
    },
    {
      icon: "❌",
      title: "Manual Order Processing Errors",
      description:
        "Long lead times and manual entry errors cost time, money, and customer satisfaction at every stage.",
    },
    {
      icon: "💸",
      title: "Rising Procurement Costs",
      description:
        "Without data-driven sourcing and spend analytics, consumer goods companies consistently overpay across every category.",
    },
    {
      icon: "📦",
      title: "Inventory Blind Spots",
      description:
        "Limited real-time visibility leads to stockouts of fast-moving items and costly dead stock in slow categories.",
    },
    {
      icon: "📈",
      title: "Seasonal Demand Mismatches",
      description:
        "Without demand forecasting, seasonal spikes cause stockouts, emergency orders, and supplier capacity issues.",
    },
    {
      icon: "📊",
      title: "Delayed Decision-Making",
      description:
        "Fragmented reports and delayed data prevent timely responses to market shifts and promotional opportunities.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
    solutionIconBg: "bg-emerald-50",
    solutionIconColor: "text-emerald-600",
    solutionAccent: "from-emerald-600 to-green-600",
  },

  solutionTitle: "Intelligent Procurement Platform for Consumer Goods Leaders",
  solutionSubtitle:
    "AI demand forecasting, supplier performance analytics, and automated ordering — purpose-built for consumer goods complexity.",
  solutions: [
    {
      icon: "🔮",
      title: "AI Demand Forecasting",
      description:
        "Predict demand for seasonal and fast-moving items using historical data and market signals — weeks in advance.",
    },
    {
      icon: "⭐",
      title: "Supplier Performance Analytics",
      description:
        "Score every supplier on delivery, quality, and pricing to make data-driven sourcing decisions that protect margins.",
    },
    {
      icon: "🤖",
      title: "Automated Order Processing",
      description:
        "Eliminate manual entry with intelligent order automation that reduces errors by 95% and cuts processing time from hours to minutes.",
    },
    {
      icon: "💰",
      title: "Dynamic Pricing Intelligence",
      description:
        "Monitor competitor and market pricing in real time to optimise your own pricing strategy and protect profitability.",
    },
    {
      icon: "📦",
      title: "Intelligent Inventory Optimisation",
      description:
        "AI maintains optimal stock levels across all SKUs, automatically reordering before stockouts while minimising excess.",
    },
    {
      icon: "📊",
      title: "Strategic Spend Analysis",
      description:
        "Identify cost reduction opportunities across every supplier and category — saving up to 25% on procurement spend.",
    },
    {
      icon: "🔄",
      title: "Product Lifecycle Management",
      description:
        "Optimise procurement from launch to end-of-life — ensuring the right stock at every stage of the product cycle.",
    },
    {
      icon: "🌐",
      title: "Multi-Supplier Consolidation",
      description:
        "Centralise all supplier relationships, catalogs, and communications in one intelligent platform — regardless of scale.",
    },
    {
      icon: "🎯",
      title: "Promotional Procurement Planning",
      description:
        "AI plans promotional inventory needs in advance, ensuring availability without costly last-minute surplus.",
    },
  ],

  howItWorksTitle: "Smarter Consumer Goods Procurement in 4 Steps",
  howItWorksSteps: [
    {
      icon: "📊",
      title: "Centralise Supplier Data",
      description: "All suppliers, catalogs, and historical orders consolidated into one AI-powered platform.",
    },
    {
      icon: "🔮",
      title: "AI Forecasts Demand",
      description: "Demand forecasting engine analyses patterns and generates SKU-level predictions automatically.",
    },
    {
      icon: "🤖",
      title: "Orders Are Automated",
      description: "Purchase orders triggered automatically based on forecasts and replenishment rules — zero manual work.",
    },
    {
      icon: "📉",
      title: "Costs Drop, Availability Rises",
      description: "Monitor spend savings, stockout rates, and supplier performance improving continuously.",
    },
  ],

  outcomeTitle: "Consumer Goods Procurement Results That Move the Needle",
  outcomeSubtitle: "Real outcomes achieved by consumer goods companies using EkkBaz AI.",
  outcomes: [
    {
      value: "25%",
      metric: "Procurement Cost Savings",
      description:
        "AI-driven supplier selection and spend analytics deliver 25% average cost reduction within the first quarter.",
    },
    {
      value: "2.5h",
      metric: "Saved Every Day",
      description:
        "Automated ordering and supplier management gives procurement staff back 2.5 hours of manual work every single day.",
    },
    {
      value: "40%",
      metric: "Lower Inventory Costs",
      description:
        "AI demand forecasting eliminates costly overstock while preventing stockouts — reducing inventory holding costs by 40%.",
    },
  ],
  outcomeImage: "/assets/images/food_solution.jpeg",

  ctaTitle: "Save 25% on Consumer Goods Procurement This Quarter",
  ctaSubtitle:
    "Book a free consultation and see how EkkBaz AI can transform your procurement operations — delivering savings in your first 90 days.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const foodFacilites = [
  {
    id: 1,
    extraLabel: "Your Challenges",
    userType: "Why Consumer Goods Procurement Is Costing You 2.5 Hours Every Day",
    description: {
      line1: `<strong>Supplier Coordination Chaos</strong> — Managing dozens of suppliers across multiple product categories with no unified system leads to missed orders and poor vendor relationships.`,
      line2: `<strong>Manual Order Processing Errors</strong> — Long lead times and frequent mistakes in manual order entry cost time, money, and customer satisfaction.`,
      line3: `<strong>Rising Procurement Costs</strong> — Without data-driven sourcing and spend analytics, consumer goods companies consistently overpay and miss cost-saving opportunities.`,
      line4: `<strong>Inventory Blind Spots</strong> — Limited real-time inventory visibility leads to stockouts of fast-moving items and dead stock in slow categories.`,
      line5: `<strong>Slow, Uninformed Decision-Making</strong> — Delayed data and fragmented reports prevent timely responses to market shifts and seasonal demand spikes.`,
    },
    banner: "/assets/images/food_problem.jpeg",
  },
  {
    id: 2,
    extraLabel: "Our Solutions",
    userType: "EkkBaz AI's Intelligent Procurement Platform for Consumer Goods",
    description: {
      line1: `<strong>AI-Powered Demand Forecasting</strong> — Predict demand for seasonal and fast-moving items using historical data and market signals to maintain optimal inventory levels.`,
      line2: `<strong>Supplier Performance Analytics</strong> — Score and rank every supplier by delivery, quality, and pricing to make data-driven sourcing decisions.`,
      line3: `<strong>Automated Order Processing</strong> — Eliminate manual entry with intelligent order automation that reduces errors by 95%.`,
      line4: `<strong>Dynamic Pricing Intelligence</strong> — Monitor competitor and market pricing in real time to optimise your pricing strategy.`,
      line5: `<strong>Strategic Spend Analysis</strong> — Identify cost reduction opportunities across every supplier and category, saving up to 25%.`,
    },
    banner: "/assets/images/food_solution.jpeg",
  },
  {
    id: 3,
    extraLabel: "Get Started",
    userType: "Save 25% on Procurement Costs Starting This Quarter",
    description: `Consumer goods companies using EkkBaz AI save an average of 25% on procurement costs, reduce inventory holding costs by 40%, and free up 2.5+ hours of procurement staff time every single day. Book a consultation to see results in your first quarter.`,
    btnLabel: "Book a Free Consultation",
    formLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    banner: "/assets/images/medicine3.png",
  },
];

export const consumerGoodsFAQs = [
  {
    question: "How does EkkBaz AI reduce procurement costs for consumer goods companies?",
    answer:
      "EkkBaz AI uses spend analytics to identify sourcing inefficiencies, AI-powered supplier scoring to select the most cost-effective vendors, and demand forecasting to prevent costly over-ordering — delivering an average of 25% cost savings within the first quarter.",
  },
  {
    question: "How does EkkBaz AI handle seasonal demand spikes in consumer goods?",
    answer:
      "EkkBaz AI's demand forecasting engine analyzes historical sales data, seasonality patterns, and market signals to predict demand spikes weeks in advance — allowing procurement teams to build appropriate inventory buffers without overstocking.",
  },
  {
    question: "Can EkkBaz AI manage multiple suppliers across different product categories?",
    answer:
      "Yes. EkkBaz AI is built for multi-supplier, multi-category procurement. The platform centralizes all supplier relationships, product catalogs, and order management in one intelligent dashboard, regardless of how many suppliers or SKUs you manage.",
  },
];
